import { ApprovedSuppliersButton } from "./ApprovedSuppliersPanel";
import React from "react";
import { api } from "../../lib/apiClient";
import { Loader2, Sparkles, ChevronDown, ClipboardCopy, Check } from "lucide-react";
import { toast } from "../ui/sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Section, Grid, Field } from "./layout";
import { CustomerAutocomplete } from "./CustomerAutocomplete";
import { SiteAutocomplete } from "./SiteAutocomplete";
import { PartsMultiSelect } from "./PartsMultiSelect";
import { PostcodeCombobox } from "./PostcodeCombobox";
import { JOB_TYPES } from "./utils";

/**
 * CustomerJobForm — the top "Customer & Job" card on the Quote Builder.
 *
 * Owns its own side-effects:
 *   - Job-type changes clear repair_parts / rewrite labour work-types.
 *   - Labour-type changes can sweep a branch's default markup % across parts & sundries.
 * Everything else is just controlled inputs reading/writing `quote` via `setQuote`.
 */
export function CustomerJobForm({
  quote,
  setQuote,
  branches,
  labourTypes,
  postcodes,
  quoteCategories,
  onShowSuppliers,
  repairParts,
  setRepairParts,
  fieldRequirements,
  isAdmin,
  costSettings,
  // AI description generation (owned by parent)
  descBusy,
  descError,
  descCopied,
  generateDescription,
  copyDescription,
  storageKey,
}) {
  const update = (patch) => setQuote((q) => ({ ...q, ...patch }));

  const onJobTypeChange = (jt) => {
    setQuote((q) => {
      if (jt === "Install") {
        return { ...q, job_type: jt, repair_parts: [] };
      }
      // Switching to Repair: replace Fresh Install / Rip Out work-types with Repair
      const labour_items = (q.labour_items || []).map((lb) =>
        (lb.work_type === "Fresh Install" || lb.work_type === "Rip Out")
          ? { ...lb, work_type: "Repair" }
          : lb
      );
      return { ...q, job_type: jt, labour_items };
    });
  };

  const onLabourTypeChange = (newBranch) => {
    const b = branches.find((x) => x.name === newBranch);
    const defMk = b && b.default_markup_percent != null ? Number(b.default_markup_percent) : null;
    // On every Labour Type change we ALSO refresh the client surcharge from the
    // new branch's `surcharge_amount`. This handles the common case of
    // swapping between key-account variants (e.g. "LS SCAS" £50 → "LS BCC"
    // £75) — previous logic only wrote the surcharge on the first pick.
    const branchSurcharge = b ? Number(b.surcharge_amount || 0) : 0;
    setQuote((q) => {
      const oldBranch = q.branch || "";
      // Sweep any labour rows that were pointing at the OLD quote-level branch
      // (or had no explicit branch and were inheriting it) onto the new branch,
      // so the rate £/hr updates immediately. Rows that a user has explicitly
      // pinned to a DIFFERENT branch are left untouched. For Repair rows we
      // also refresh `_auto_hours` from the new branch's min_repair_hours
      // (or the global default) so the backend floor uses the correct value.
      const globalRepairMin = Number(costSettings?.repair_minimum_hours ?? 4.5);
      const branchRepairMin = b && b.min_repair_hours != null
        ? Number(b.min_repair_hours)
        : globalRepairMin;
      const nextLabour = (q.labour_items || []).map((lb) => {
        const linePinned = lb.branch && lb.branch !== oldBranch;
        if (linePinned) return lb;
        const patch = { ...lb, branch: newBranch };
        if ((lb.work_type || "") === "Repair") {
          patch._auto_hours = branchRepairMin;
        }
        return patch;
      });
      return {
        ...q,
        branch: newBranch,
        labour_items: nextLabour,
        markup_percent: defMk != null ? defMk : q.markup_percent,
        line_items: defMk != null
          ? q.line_items.map((li) => ({ ...li, markup_percent: defMk }))
          : q.line_items,
        sundries: defMk != null
          ? (q.sundries || []).map((s) => ({ ...s, markup_percent: defMk }))
          : q.sundries,
        customer_surcharge: branchSurcharge,
      };
    });
  };

  const onCreatePart = async (name) => {
    try {
      const { data } = await api.post("/repair-parts", {
        name,
        categories: quote.category ? [quote.category] : [],
      });
      const url = quote.category
        ? `/repair-parts?category=${encodeURIComponent(quote.category)}`
        : "/repair-parts";
      const refreshed = await api.get(url);
      setRepairParts(refreshed.data || []);
      const next = Array.from(new Set([...(quote.repair_parts || []), data.name])).sort();
      update({ repair_parts: next });
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create part");
      throw e;
    }
  };

  return (
    <Section title="Customer & Job" collapsible storageKey={storageKey}>
      <Grid>
        <Field label="Customer Name" testid="field-customer" required={fieldRequirements.customer_name}>
          <CustomerAutocomplete
            value={quote.customer_name}
            onChange={(name) => update({ customer_name: name, customer_id: null })}
            onPickCustomer={(c) => update({
              customer_name: c.name,
              customer_id: c.id || null,
              // Auto-seed the client surcharge from the customer default. If
              // the estimator has already typed a manual value, don't clobber.
              ...((quote.customer_surcharge == null || Number(quote.customer_surcharge) === 0)
                ? { customer_surcharge: Number(c.surcharge_amount || 0) }
                : {}),
            })}
          />
        </Field>
        <Field label="Site Name" testid="field-site-name">
          <SiteAutocomplete
            customerName={quote.customer_name}
            value={quote.site_name}
            onChange={(v) => update({ site_name: v })}
            onPick={(s) => update({
              site_name: s.site_name || "",
              site_address: s.site_address || quote.site_address || "",
              postcode: s.postcode || quote.postcode || "",
            })}
          />
        </Field>
        <Field label="Site Address" testid="field-site-address">
          <input
            value={quote.site_address || ""}
            onChange={(e) => update({ site_address: e.target.value })}
            className="qb-input"
            data-testid="site-address-input"
            placeholder="Street, town"
          />
        </Field>
        <Field label="Quote Number" required={fieldRequirements.quote_number}>
          <div className="qb-input flex items-center gap-0 p-0 overflow-hidden focus-within:ring-2 focus-within:ring-brand-blue/40 focus-within:border-brand-blue" data-testid="quote-number-prefix-wrap">
            <span
              className="pl-3 pr-1 h-full flex items-center text-slate-400 font-mono text-sm select-none"
              aria-hidden="true"
            >QUO/</span>
            <input
              value={(quote.quote_number || "").replace(/^QUO[\/-]?/i, "")}
              onChange={(e) => {
                const raw = e.target.value.replace(/^QUO[\/-]?/i, "");
                update({ quote_number: raw ? `QUO/${raw}` : "" });
              }}
              className="flex-1 bg-transparent border-0 outline-none font-mono text-sm pr-3 py-2"
              data-testid="quote-number-input"
              placeholder=""
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5 italic" data-testid="quote-number-helper">
            Please use QUO/ number from CRM System
          </p>
        </Field>
        <Field label="Job Type" required={fieldRequirements.job_type}>
          <select
            value={quote.job_type}
            onChange={(e) => onJobTypeChange(e.target.value)}
            className="qb-input"
            data-testid="job-type-select"
          >
            {JOB_TYPES.map((j) => <option key={j}>{j}</option>)}
          </select>
        </Field>
        <Field label="Category" required={fieldRequirements.category}>
          <select
            value={quote.category || ""}
            onChange={(e) => update({ category: e.target.value, repair_parts: [] })}
            className="qb-input"
            data-testid="category-select"
          >
            <option value="">— pick a category —</option>
            {quoteCategories.map((c) => <option key={c.id}>{c.name}</option>)}
          </select>
          {quote.category && onShowSuppliers && <ApprovedSuppliersButton onClick={onShowSuppliers} />}
        </Field>
        <Field label="Estimator" required={fieldRequirements.estimator}>
          <input
            value={quote.estimator}
            onChange={(e) => update({ estimator: e.target.value })}
            className="qb-input"
            data-testid="estimator-input"
            placeholder="Your name"
          />
        </Field>
        <Field label="Labour Type" required={fieldRequirements.branch}>
          <select
            value={quote.branch}
            onChange={(e) => onLabourTypeChange(e.target.value)}
            className="qb-input"
            data-testid="labour-type-select"
          >
            <option value="">—</option>
            {labourTypes.map((b) => <option key={b.id}>{b.name}</option>)}
          </select>
        </Field>
        <Field label="Postcode" required={fieldRequirements.postcode}>
          <PostcodeCombobox
            value={quote.postcode}
            onChange={(v) => update({ postcode: v })}
            postcodes={postcodes}
          />
        </Field>
        {quote.job_type === "Repair" && !quote.labour_only && (
          <Field label="Parts required" required={fieldRequirements.repair_parts}>
            <PartsMultiSelect
              options={repairParts}
              selected={quote.repair_parts || []}
              onChange={(next) => update({ repair_parts: next })}
              isAdmin={isAdmin}
              activeCategory={quote.category}
              onCreate={onCreatePart}
            />
          </Field>
        )}
        <Field label="Engineers required" required={fieldRequirements.engineers}>
          <select
            value={Number(quote.engineers || 1)}
            onChange={(e) => update({ engineers: Number(e.target.value) || 1 })}
            className="qb-input"
            data-testid="engineers-select"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </Field>
        <Field label="Brief description of works" colSpan={3} required={fieldRequirements.description}>
          <div className="relative">
            <textarea
              value={quote.description}
              onChange={(e) => update({ description: e.target.value })}
              className="qb-input min-h-[96px] py-2 pr-2"
              data-testid="description-input"
              placeholder="e.g. Engineers to attend site, create a safe working area…"
            />
            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                {descError ? (
                  <span className="text-red-600" data-testid="desc-error">{descError}</span>
                ) : descBusy ? (
                  <span className="text-brand-blue">Claude is drafting your description…</span>
                ) : descCopied ? (
                  <span className="text-green-700">Copied to clipboard</span>
                ) : (
                  <span>Generate a draft based on customer, branch, materials & labour selected above.</span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex" data-testid="generate-split-button">
                  <button
                    type="button"
                    onClick={() => generateDescription("default")}
                    disabled={descBusy}
                    data-testid="generate-description-button"
                    className="h-8 px-3 bg-brand-blue text-white hover:bg-brand-blue-dark text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 disabled:opacity-60 border-r border-blue-300"
                  >
                    {descBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {descBusy ? "Generating…" : quote.description ? "Regenerate" : "Generate with AI"}
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        disabled={descBusy}
                        data-testid="generate-variation-trigger"
                        aria-label="Choose variation"
                        className="h-8 px-1.5 bg-brand-blue text-white hover:bg-brand-blue-dark inline-flex items-center disabled:opacity-60"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 font-display">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                        Generate a variation
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => generateDescription("shorter")} data-testid="variation-shorter">
                        <span className="font-medium">Shorter</span>
                        <span className="ml-auto text-[10px] text-slate-500">2 sentences</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => generateDescription("longer")} data-testid="variation-longer">
                        <span className="font-medium">Longer</span>
                        <span className="ml-auto text-[10px] text-slate-500">6–8 sentences</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => generateDescription("more_technical")} data-testid="variation-technical">
                        <span className="font-medium">More technical</span>
                        <span className="ml-auto text-[10px] text-slate-500">trade terms</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => generateDescription("more_friendly")} data-testid="variation-friendly">
                        <span className="font-medium">More friendly</span>
                        <span className="ml-auto text-[10px] text-slate-500">plain English</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <button
                  type="button"
                  onClick={copyDescription}
                  disabled={!quote.description}
                  data-testid="copy-description-button"
                  className="h-8 px-3 border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                  title="Copy description to clipboard"
                >
                  {descCopied ? <Check size={12} /> : <ClipboardCopy size={12} />}
                  {descCopied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>
        </Field>
      </Grid>
    </Section>
  );
}
